import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { jwtDecode } from 'jwt-decode';
import { useRefreshApi } from '../../hooks';
import { useTokens } from '../tokens-provider';
import { SocketSubscriptionMap } from './models';
import { WebsocketContext } from './context';
import { useRegion } from '../region-provider';

/**
 * Provider
 */
type Props = {
    readonly children?: ReactNode;
};
function WebsocketProvider({ children }: Props): ReactElement {
    const { config } = useRegion();
    const baseUrl = config.apiBaseUrl;
    const socketBaseUrl = config.socketBaseUrl;

    const {
        tokens: { accessToken, refreshToken }
    } = useTokens();

    const socket = useMemo<Socket>(() => {
        return io(socketBaseUrl, {
            transports: ['websocket'],
            path: '/v2/sockets/socket.io/',
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            randomizationFactor: 0.5,
            timeout: 20000
        });
    }, [socketBaseUrl]);

    const { refreshTokenApi } = useRefreshApi({
        baseUrl,
        refreshToken,
        refreshUrl: 'v3/auth/refresh'
    });

    const [state, setState] = useState<{
        connected: boolean;
        authenticated: boolean;
    }>({
        connected: false,
        authenticated: false
    });
    const [subscriptions, setSubscriptions] = useState<SocketSubscriptionMap>(
        {}
    );

    useEffect(() => {
        const connectedSymbol = state.connected ? '✅' : '❌';
        const authenticatedSymbol = state.authenticated ? '✅' : '❌';
        console.log(
            `Socket status. Connected: ${connectedSymbol}, Authenticated: ${authenticatedSymbol}`
        );
    }, [state.connected, state.authenticated]);

    useEffect(() => {
        if (!state.authenticated) {
            return;
        }

        // Build list of unique resources with a count where scheduleForRemoval=false
        interface SubscriptionCountMap {
            [resource: string]: { resource: string; count: number };
        }
        const subscriptionsWithCount = Object.values(
            subscriptions
        ).reduce<SubscriptionCountMap>((map, item) => {
            const existingItem = map[item.resource];
            if (existingItem) {
                map[item.resource] = {
                    ...existingItem,
                    count: item.scheduleForRemoval
                        ? existingItem.count
                        : existingItem.count + 1
                };
            } else {
                map[item.resource] = {
                    resource: item.resource,
                    count: item.scheduleForRemoval ? 0 : 1
                };
            }
            return map;
        }, {});

        // Subscribe to resources where count>0
        const toSubscribe: Array<{ resource: string }> = Object.values(
            subscriptionsWithCount
        )
            .filter((s) => s.count > 0)
            .map((s) => ({ resource: s.resource }));
        if (toSubscribe.length > 0) {
            const event = 'subscriptions_add';
            socket.emit(
                event,
                { subscriptions: toSubscribe },
                (response: { status: string; message: string }) => {
                    if (response.status === 'ERROR') {
                        console.error(
                            `Websocket failed to subscribe to ${JSON.stringify({
                                subscriptions: toSubscribe
                            })}. Error message: ${response.message}`
                        );
                    }
                }
            );
        }

        // Unsubscribe from resources where count=0
        const toUnsubscribe: Array<{ resource: string }> = Object.values(
            subscriptionsWithCount
        )
            .filter((s) => s.count === 0)
            .map((s) => ({ resource: s.resource }));
        if (toUnsubscribe.length > 0) {
            const event = 'subscriptions_remove';
            socket.emit(
                event,
                { subscriptions: toUnsubscribe },
                (response: { status: string; message: string }) => {
                    if (response.status === 'ERROR') {
                        console.error(
                            `Websocket failed to unsubscribe from ${JSON.stringify(
                                {
                                    subscriptions: toUnsubscribe
                                }
                            )}. Error message: ${response.message}`
                        );
                    }
                }
            );
        }
    }, [socket, state, subscriptions]);

    useEffect(() => {
        const onConnectCallback = async () => {
            setState((current) => ({
                ...current,
                connected: true
            }));

            let token = accessToken;

            const payload = jwtDecode(accessToken);
            // If access token is expired or expires in the next 5 seconds, refresh it
            if (payload.exp && payload.exp * 1000 <= Date.now() + 5000) {
                token = await refreshTokenApi().catch(() => accessToken);
            }

            const event = 'authenticate';
            socket.emit(
                event,
                { accessToken: token },
                (response: { status: string; message: string }) => {
                    if (response.status === 'ERROR') {
                        // console.error({
                        // 	message: `Failed to authenticate with the socket server. Error message: ${response.message}`,
                        // 	context: LOG_CONTEXT
                        // });
                    }
                }
            );
        };

        const onReauthenticateCallback = async () => {
            const accessToken = await refreshTokenApi().catch(() => null);
            if (!accessToken) {
                return;
            }
            const event = 'authenticate';
            socket.emit(
                event,
                { accessToken },
                (response: { status: string; message: string }) => {
                    if (response.status === 'ERROR') {
                        // console.error({
                        // 	message: `Failed to authenticate with the socket server. Error message: ${response.message}`,
                        // 	context: LOG_CONTEXT
                        // });
                    }
                }
            );
        };

        const onDisconnectCallback = () => {
            setState((current) => ({
                ...current,
                connected: false,
                authenticated: false
            }));
        };

        const onAuthenticatedCallback = () => {
            setState((current) => ({
                ...current,
                authenticated: current.connected // If connected, we're authenticated
            }));
        };

        socket.on('connect', onConnectCallback);
        socket.on('disconnect', onDisconnectCallback);
        socket.on('authenticated', onAuthenticatedCallback);
        socket.on('re-authenticate', onReauthenticateCallback);

        return () => {
            socket.off('connect', onConnectCallback);
            socket.off('disconnect', onDisconnectCallback);
            socket.off('authenticated', onAuthenticatedCallback);
            socket.off('re-authenticate', onReauthenticateCallback);
        };
    }, [socket, accessToken, refreshTokenApi]);

    return (
        <WebsocketContext.Provider
            value={{
                connected: state.connected,
                authenticated: state.authenticated,
                socket,
                setSubscriptions
            }}
        >
            {children}
        </WebsocketContext.Provider>
    );
}

export { WebsocketProvider };
